<template>
  <v-container fluid id="truckRequestsV2" class="pa-0 pa-sm-3">
    <v-row wrap justify="center">
      <v-col cols="12" class="headline pb-2">
        Truck Scheduler
        <v-btn
          small
          color="primary"
          class="mx-4"
          :to="'/truck-requests/manage/orders'"
        >
          <v-icon left small>arrow_back</v-icon>Back
        </v-btn>
        <v-btn small color="success" class="mx-2" @click="showPreviewDialog">
          <v-icon left small>visibility</v-icon>Preview Schedule
        </v-btn>
        <v-btn small color="primary" class="mx-2" @click="printSchedule">
          <v-icon left small>print</v-icon>Print Schedule
        </v-btn>
        <v-menu
          ref="menu"
          v-model="dateMenu"
          :close-on-content-click="false"
          :return-value.sync="selectedDate"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              small
              outlined
              color="primary"
              class="mx-2"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon left small>event</v-icon>
              {{ selectedDate | moment("MMM Do, YYYY") }}
            </v-btn>
          </template>
          <v-date-picker
            v-model="selectedDate"
            scrollable
            color="primary"
            :min="minDate"
          >
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="$refs.menu.save(selectedDate)">
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>
        <div class="borough-legend">
          <span class="legend-item">
            <span class="legend-dot" style="background-color: #2196F3"></span>
            <span class="legend-text">Bronx</span>
          </span>
          <span class="legend-item">
            <span class="legend-dot" style="background-color: #FF9800"></span>
            <span class="legend-text">Queens</span>
          </span>
          <span class="legend-item">
            <span class="legend-dot" style="background-color: #F44336"></span>
            <span class="legend-text">Brooklyn</span>
          </span>
          <span class="legend-item">
            <span class="legend-dot" style="background-color: #9E9E9E"></span>
            <span class="legend-text">Manhattan</span>
          </span>
          <span class="legend-item">
            <span class="legend-dot" style="background-color: #795548"></span>
            <span class="legend-text">Staten Island</span>
          </span>
        </div>
      </v-col>

      <!-- Main Scheduler Section -->
      <v-col cols="12" class="py-0">
        <v-card>
          <v-card-title class="py-2">
            <span class="subtitle-1"
              >Schedule for {{ selectedDate | moment("dddd, MMM Do") }}</span
            >
            <v-text-field
              v-model="driverFilter"
              label="Filter by driver"
              hide-details
              dense
              outlined
              clearable
              prepend-inner-icon="search"
              class="ml-4"
              style="max-width: 250px;"
            ></v-text-field>
            <v-select
              v-model="driverTypeFilter"
              :items="driverTypeFilterOptions"
              label="Driver Type"
              hide-details
              dense
              outlined
              class="ml-2"
              style="max-width: 200px;"
            ></v-select>
            <v-spacer></v-spacer>
            <v-chip small color="info" class="mx-1">
              {{ displayDrivers.length }} Drivers
            </v-chip>
            <v-chip small color="success" class="mx-1">
              {{ scheduledRequestsCount }} Scheduled
            </v-chip>
            <v-chip small color="grey white--text" class="mx-1">
              {{ closedRequestsCount }} Closed
            </v-chip>
            <v-chip small color="warning" class="mx-1">
              {{ unscheduledRequests.length }} Unscheduled
            </v-chip>
          </v-card-title>
          <v-divider></v-divider>

          <!-- Main Scheduler Layout -->
          <div class="scheduler-layout">
            <!-- Left Panel: Unscheduled Requests -->
            <div class="unscheduled-panel">
              <div class="panel-header">
                <span class="panel-title">Unscheduled Requests</span>
                <v-chip
                  small
                  :color="
                    unscheduledRequests.length > 0 ? 'warning' : 'success'
                  "
                >
                  {{ unscheduledRequests.length }}
                </v-chip>
              </div>
              <div
                class="unscheduled-list-vertical"
                @drop="handleUnscheduleDrop($event)"
                @dragover.prevent="handleUnscheduleDragOver($event)"
                @dragenter.prevent="handleUnscheduleDragEnter($event)"
                @dragleave="handleUnscheduleDragLeave($event)"
              >
                <div
                  v-for="request in unscheduledRequests"
                  :key="request.orderNumber"
                  class="job-card unscheduled vertical"
                  draggable="true"
                  @dragstart="handleDragStart($event, request)"
                  :style="{
                    borderLeft: `4px solid ${getBoroughColor(request.jobSite)}`,
                  }"
                >
                  <div class="job-minimal">
                    <div class="job-time-row">
                      <span
                        class="job-time clickable"
                        @click.stop="openJobDetails(request)"
                        >{{ request.requestTime || "Flexible" }}</span
                      >
                      <span class="job-qty">{{ formatJobQty(request) }}</span>
                    </div>
                    <span class="job-address-minimal">{{
                      request.jobSite.street
                    }}</span>
                  </div>
                </div>

                <div
                  v-if="unscheduledRequests.length === 0"
                  class="no-requests"
                >
                  <v-icon color="grey" class="mb-2">check_circle</v-icon>
                  <div class="grey--text text-center">
                    All requests scheduled
                  </div>
                </div>

                <!-- Drop zone hint when dragging scheduled job -->
                <div class="unschedule-drop-hint" v-show="false">
                  Drop here to unschedule
                </div>
              </div>
            </div>

            <!-- Right Panel: Schedule Grid -->
            <div class="schedule-panel">
              <!-- Schedule Grid -->
              <div class="schedule-wrapper">
                <div class="schedule-container">
                  <!-- Header Row -->
                  <div class="schedule-header">
                    <div class="time-header">Time</div>
                    <div
                      v-for="driver in displayDrivers"
                      :key="driver.id"
                      class="driver-header"
                    >
                      <div class="driver-name">{{ driver.name }}</div>
                      <div class="driver-stats">
                        {{ getDriverScheduledCount(driver.id) }} jobs
                        <span v-if="driverHoursMap[driver.id]" class="driver-hours">({{ driverHoursMap[driver.id].toFixed(1) }}hrs)</span>
                      </div>
                    </div>
                  </div>

                  <!-- Schedule Body -->
                  <div class="schedule-body">
                    <div
                      v-for="timeSlot in timeSlots"
                      :key="timeSlot.value"
                      class="schedule-row"
                    >
                      <!-- Time Label -->
                      <div class="time-cell">
                        {{ timeSlot.display }}
                      </div>

                      <!-- Driver Columns -->
                      <div
                        v-for="driver in displayDrivers"
                        :key="`${timeSlot.value}-${driver.id}`"
                        class="schedule-cell"
                        :class="{
                          'cell-occupied': isSlotOccupied(
                            driver.id,
                            timeSlot.value
                          ),
                          'cell-drag-over': false,
                        }"
                        @drop="handleDrop($event, driver.id, timeSlot.value)"
                        @dragover.prevent="handleDragOver($event)"
                        @dragenter.prevent="handleDragEnter($event)"
                        @dragleave="handleDragLeave($event)"
                      >
                        <!-- Scheduled Job Card -->
                        <div
                          v-if="getScheduledJob(driver.id, timeSlot.value)"
                          class="job-card scheduled"
                          :class="{
                            'job-closed':
                              getScheduledJob(driver.id, timeSlot.value)
                                .status === 'closed',
                            'job-draft':
                              getScheduledJob(driver.id, timeSlot.value)
                                .isDraft,
                            'job-locked':
                              getScheduledJob(driver.id, timeSlot.value)
                                .status === 'scheduled' &&
                              !isJobUnlocked(getScheduledJob(driver.id, timeSlot.value).orderNumber),
                          }"
                          :draggable="isJobDraggable(getScheduledJob(driver.id, timeSlot.value))"
                          @dragstart="
                            handleScheduledDragStart(
                              $event,
                              getScheduledJob(driver.id, timeSlot.value),
                              driver.id,
                              timeSlot.value
                            )
                          "
                          :title="isJobDraggable(getScheduledJob(driver.id, timeSlot.value)) ? 'Drag to move' : 'Click time to unlock'"
                          :style="{
                            borderLeft: `4px solid ${getBoroughColor(
                              getScheduledJob(driver.id, timeSlot.value).jobSite
                            )}`,
                          }"
                        >
                          <div class="job-minimal">
                            <div class="job-time-row">
                              <span
                                class="job-time clickable"
                                @click.stop="openJobDetails(getScheduledJob(driver.id, timeSlot.value))"
                                >{{
                                  getScheduledJob(driver.id, timeSlot.value)
                                    .requestTime || timeSlot.value
                                }}</span
                              >
                              <v-icon
                                v-if="getScheduledJob(driver.id, timeSlot.value).status === 'scheduled' && !isJobUnlocked(getScheduledJob(driver.id, timeSlot.value).orderNumber)"
                                x-small
                                class="job-lock-icon"
                              >lock</v-icon>
                              <span class="job-qty">{{
                                getScheduledJob(driver.id, timeSlot.value).status === 'closed'
                                  ? formatClosedJobQty(getScheduledJob(driver.id, timeSlot.value))
                                  : formatJobQty(getScheduledJob(driver.id, timeSlot.value))
                              }}</span>
                            </div>
                            <span class="job-address-minimal">{{
                              getScheduledJob(driver.id, timeSlot.value).jobSite
                                .street
                            }}</span>
                            <span
                              v-if="getScheduledJob(driver.id, timeSlot.value).status === 'closed' && formatActualRange(getScheduledJob(driver.id, timeSlot.value))"
                              class="job-actual-range"
                            >
                              <v-icon x-small class="job-actual-icon">access_time</v-icon>
                              {{ formatActualRange(getScheduledJob(driver.id, timeSlot.value)) }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar for feedback -->
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">Close</v-btn>
    </v-snackbar>

    <!-- Preview Schedule Dialog -->
    <v-dialog v-model="previewDialog" max-width="900px">
      <v-card>
        <v-card-title class="headline">
          Schedule Preview - {{ selectedDate | moment("MMM Do, YYYY") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="previewDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6">
          <!-- Packer Drivers Section -->
          <div v-if="packerSchedule.length > 0" class="mb-8">
            <h2 class="preview-section-header">Demo Packers</h2>
            <v-simple-table class="schedule-preview-table">
              <thead>
                <tr>
                  <th class="text-left col-address">Address</th>
                  <th class="text-left col-job-id">Job ID</th>
                  <th class="text-left col-time">Time</th>
                  <th class="text-left col-details">Details</th>
                  <th class="text-left col-driver">Driver</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(job, index) in packerSchedule"
                  :key="`packer-${index}`"
                >
                  <td>
                    <div>{{ job.address }}</div>
                    <div v-if="job.qty" class="qty-text">
                      {{ job.qty }}
                    </div>
                    <div v-if="job.client" class="client-name">
                      {{ job.client }}
                    </div>
                  </td>
                  <td class="job-id-cell">{{ job.jobId }}</td>
                  <td>{{ job.time }}</td>
                  <td>{{ job.details }}</td>
                  <td class="driver-cell">{{ job.driver }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- Roll Off Drivers Section -->
          <div v-if="rollOffSchedule.length > 0">
            <h2 class="preview-section-header">Roll Offs</h2>
            <v-simple-table class="schedule-preview-table">
              <thead>
                <tr>
                  <th class="text-left col-address">Address</th>
                  <th class="text-left col-job-id">Job ID</th>
                  <th class="text-left col-time">Time</th>
                  <th class="text-left col-details">Details</th>
                  <th class="text-left col-driver">Driver</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(job, index) in rollOffSchedule"
                  :key="`rolloff-${index}`"
                >
                  <td>
                    <div>{{ job.address }}</div>
                    <div v-if="job.qty" class="qty-text">
                      {{ job.qty }}
                    </div>
                    <div v-if="job.client" class="client-name">
                      {{ job.client }}
                    </div>
                  </td>
                  <td class="job-id-cell">{{ job.jobId }}</td>
                  <td>{{ job.time }}</td>
                  <td>{{ job.details }}</td>
                  <td class="driver-cell">{{ job.driver }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- Outside Haulers Section -->
          <div v-if="outsideHaulerSchedule.length > 0" class="mt-8">
            <h2 class="preview-section-header">Outside Haulers</h2>
            <v-simple-table class="schedule-preview-table">
              <thead>
                <tr>
                  <th class="text-left col-address">Address</th>
                  <th class="text-left col-job-id">Job ID</th>
                  <th class="text-left col-time">Time</th>
                  <th class="text-left col-details">Details</th>
                  <th class="text-left col-driver">Hauler</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(job, index) in outsideHaulerSchedule"
                  :key="`hauler-${index}`"
                >
                  <td>
                    <div>{{ job.address }}</div>
                    <div v-if="job.qty" class="qty-text">
                      {{ job.qty }}
                    </div>
                    <div v-if="job.client" class="client-name">
                      {{ job.client }}
                    </div>
                  </td>
                  <td class="job-id-cell">{{ job.jobId }}</td>
                  <td>{{ job.time }}</td>
                  <td>{{ job.details }}</td>
                  <td class="driver-cell">{{ job.driver }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <div
            v-if="
              packerSchedule.length === 0 &&
                rollOffSchedule.length === 0 &&
                outsideHaulerSchedule.length === 0
            "
            class="text-center grey--text pa-4"
          >
            No scheduled jobs for this date
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="previewDialog = false">
            Close
          </v-btn>
          <v-btn color="primary" @click="finalizeAndSendSchedule">
            <v-icon left small>send</v-icon>Finalize and Send Schedule
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Job Details Dialog -->
    <v-dialog v-model="jobDetailsDialog" max-width="500px">
      <v-card v-if="selectedJob">
        <v-card-title class="headline">
          Job Details
          <v-btn
            x-small
            outlined
            color="grey"
            class="ml-3"
            :href="`/truck-requests/manage/orders/${selectedJob.orderNumber}`"
            target="_blank"
          >
            View
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn icon @click="jobDetailsDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <div class="job-details-row">
            <strong>Order #:</strong> {{ selectedJob.orderNumber }}
          </div>
          <div class="job-details-row">
            <strong>Address:</strong> {{ selectedJob.jobSite.street }}
          </div>
          <div class="job-details-row">
            <strong>Job ID:</strong> {{ selectedJob.jobSite.jobId }}
          </div>
          <div class="job-details-row" v-if="selectedJob.jobSite.client">
            <strong>Client:</strong> {{ selectedJob.jobSite.client.name }}
          </div>
          <div class="job-details-row">
            <strong>Requested Time:</strong>
            {{ selectedJob.requestTime || "Flexible" }}
          </div>
          <div class="job-details-row">
            <strong>Request Date:</strong>
            {{ selectedJob.requestDate | moment("MMM Do, YYYY") }}
          </div>
          <template v-if="selectedJob.status === 'closed'">
            <v-divider class="my-3"></v-divider>
            <div class="job-details-row" v-if="formatActualRange(selectedJob)">
              <strong>Actual Time:</strong> {{ formatActualRange(selectedJob) }}
            </div>
            <div class="job-details-row" v-if="aggregateDumpTickets(selectedJob).tons">
              <strong>Weight:</strong> {{ aggregateDumpTickets(selectedJob).tons }} T
            </div>
            <div class="job-details-row" v-if="aggregateDumpTickets(selectedJob).dockets.length">
              <strong>Dockets:</strong> {{ aggregateDumpTickets(selectedJob).dockets.join(", ") }}
            </div>
          </template>
          <v-divider class="my-3"></v-divider>
          <TruckRequestDetails :order="selectedJob" :showNotes="true" />
          <v-alert
            v-if="selectedJob.isDraft"
            type="warning"
            dense
            class="mt-4 mb-0"
          >
            This request is in draft state. The driver has not been officially assigned yet.
          </v-alert>
          <v-alert
            v-if="selectedJob.status === 'scheduled'"
            :type="isJobUnlocked(selectedJob.orderNumber) ? 'success' : 'info'"
            dense
            class="mt-4 mb-0"
          >
            <div class="d-flex align-center justify-space-between">
              <span v-if="!isJobUnlocked(selectedJob.orderNumber)">
                <v-icon small class="mr-1">lock</v-icon>
                This scheduled job is locked.
              </span>
              <span v-else>
                <v-icon small class="mr-1">lock_open</v-icon>
                This job is unlocked and can be moved.
              </span>
              <v-btn
                x-small
                :color="isJobUnlocked(selectedJob.orderNumber) ? 'grey' : 'primary'"
                @click="toggleJobLock(selectedJob.orderNumber)"
                class="ml-2"
              >
                {{ isJobUnlocked(selectedJob.orderNumber) ? 'Lock' : 'Unlock to move' }}
              </v-btn>
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="jobDetailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import fb from "@/main";
import firebase from "firebase/app";
import moment from "moment";
import { mapGetters, mapState } from "vuex";
import TruckRequestDetails from "@/components/truckRequests/truckRequestDetails";
import { isInCongestionZone } from "@/helpers/congestionZones";

export default {
  components: {
    TruckRequestDetails,
  },
  data() {
    return {
      selectedDate: moment()
        .add(1, "day")
        .format("YYYY-MM-DD"), // Default to tomorrow
      dateMenu: false,
      drivers: [],
      driverFilter: "",
      driverTypeFilter: "All",
      driverTypeFilterOptions: [
        "All",
        "Packer Driver",
        "Roll Off Driver",
        "Outside Hauler",
      ],
      localSchedule: {}, // driver_id -> time_slot -> truck_request
      locallyUnscheduled: [], // Items that were unscheduled from the schedule
      locallyScheduled: [], // Track orderNumbers of items that have been scheduled
      timeSlots: [],
      snack: false,
      snackColor: "",
      snackText: "",
      loadingRequests: true,
      previewDialog: false,
      jobDetailsDialog: false,
      selectedJob: null,
      unlockedJobs: [], // Track orderNumbers of scheduled jobs that have been unlocked for moving
      driverHoursMap: {}, // driver user ID → hours (number), loaded in background
    };
  },
  computed: {
    ...mapState(["truckRequests", "user"]),
    ...mapGetters(["truckRequestsOpen", "truckRequestsScheduled"]),
    todayDate() {
      return moment().format("YYYY-MM-DD");
    },
    minDate() {
      return moment()
        .subtract(7, "days")
        .format("YYYY-MM-DD");
    },
    unscheduledRequests() {
      // Get original open requests for the selected date
      const filtered = this.truckRequestsOpen.filter((request) => {
        // Filter requests for the selected date or flexible timing
        const requestDate = moment(request.requestDate).format("YYYY-MM-DD");
        const selectedDate = this.selectedDate;

        // Include requests for the selected date or requests without specific dates
        const matchesDate =
          requestDate === selectedDate || !request.requestDate;

        // Exclude items that have been locally scheduled
        const notLocallyScheduled = !this.locallyScheduled.includes(
          request.orderNumber
        );

        // Exclude items that have draft assignments
        const notDrafted = !request.draftDriver || !request.draftTime;

        return matchesDate && notLocallyScheduled && notDrafted;
      });

      // Add locally unscheduled items
      const combined = [...filtered, ...this.locallyUnscheduled];

      // Remove duplicates based on orderNumber
      const unique = combined.filter(
        (request, index, self) =>
          index === self.findIndex((r) => r.orderNumber === request.orderNumber)
      );

      // Sort by requestTime, putting earlier times first
      return unique.sort((a, b) => {
        // Handle cases where requestTime might be null/undefined
        if (!a.requestTime && !b.requestTime) return 0;
        if (!a.requestTime) return 1; // Put requests without time at the end
        if (!b.requestTime) return -1; // Put requests without time at the end

        // Convert time strings to moments for comparison
        const timeA = moment(a.requestTime, "h:mm A");
        const timeB = moment(b.requestTime, "h:mm A");

        return timeA.diff(timeB);
      });
    },
    scheduledRequestsForDate() {
      // Include both scheduled and closed requests
      const scheduledRequests = this.truckRequestsScheduled.filter(
        (request) => {
          if (!request.scheduledDay) return false;
          const scheduledDate = moment(request.scheduledDay.day).format(
            "YYYY-MM-DD"
          );
          return scheduledDate === this.selectedDate;
        }
      );

      const closedRequests = this.$store.getters.truckRequestsClosed.filter(
        (request) => {
          if (!request.scheduledDay) return false;
          const scheduledDate = moment(request.scheduledDay.day).format(
            "YYYY-MM-DD"
          );
          return scheduledDate === this.selectedDate;
        }
      );

      return [...scheduledRequests, ...closedRequests];
    },
    draftedRequestsForDate() {
      return this.truckRequestsOpen.filter((request) => {
        // Has draft assignment
        if (!request.draftDriver || !request.draftTime) return false;

        // Matches selected date
        const requestDate = moment(request.requestDate).format("YYYY-MM-DD");
        return requestDate === this.selectedDate || !request.requestDate;
      });
    },
    cancelledRequestsForDate() {
      return this.$store.getters.truckRequestsCancelled.filter((request) => {
        if (request.scheduledDay && request.scheduledDay.day) {
          const scheduledDate = moment(request.scheduledDay.day).format(
            "YYYY-MM-DD"
          );
          return scheduledDate === this.selectedDate;
        }
        if (request.requestDate) {
          const requestDate = moment(request.requestDate).format("YYYY-MM-DD");
          return requestDate === this.selectedDate;
        }
        return false;
      });
    },
    scheduledRequestsCount() {
      return this.scheduledRequestsForDate.filter(
        (r) => r.status === "scheduled"
      ).length;
    },
    closedRequestsCount() {
      return this.scheduledRequestsForDate.filter((r) => r.status === "closed")
        .length;
    },
    displayDrivers() {
      let filtered = this.drivers;

      // Filter by driver type
      if (this.driverTypeFilter !== "All") {
        filtered = filtered.filter((driver) => {
          return (
            driver.driverTypes &&
            driver.driverTypes.includes(this.driverTypeFilter)
          );
        });
      }

      // Filter by name search
      if (this.driverFilter) {
        const filter = this.driverFilter.toLowerCase();
        filtered = filtered.filter((driver) =>
          driver.name.toLowerCase().includes(filter)
        );
      }

      return filtered;
    },
    packerSchedule() {
      return this.getScheduleByDriverType("Packer Driver");
    },
    rollOffSchedule() {
      return this.getScheduleByDriverType("Roll Off Driver");
    },
    outsideHaulerSchedule() {
      return this.getScheduleByDriverType("Outside Hauler");
    },
  },
  async created() {
    if (!this.truckRequests.length) {
      await this.$store.dispatch("getTruckRequests", fb);
    }
    this.loadingRequests = false;
    this.generateTimeSlots();
    await this.getDrivers();
    // Wait for computed properties to be ready before building schedule
    await this.$nextTick();
    this.buildScheduleGrid();
    // Load driver hours in background — does not block rendering
    this.loadDriverHoursBackground();
  },
  methods: {
    generateTimeSlots() {
      const slots = [];
      // Generate 30-minute slots from 5 AM to 6 PM
      for (let hour = 5; hour <= 18; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const time = moment()
            .hour(hour)
            .minute(minute);
          slots.push({
            value: time.format("HH:mm"),
            display: time.format("h:mm A"),
          });
        }
      }
      this.timeSlots = slots;
    },

    async getDrivers() {
      try {
        const resDrivers = await fb.db
          .collection("users")
          .where("role", "==", "Driver")
          .get();
        let drivers = [];
        resDrivers.forEach((d) => {
          drivers.push({
            id: d.id,
            ...d.data(),
          });
        });

        // Filter to only include drivers with driverTypes defined
        drivers = drivers.filter(
          (d) => d.driverTypes && d.driverTypes.length > 0
        );

        // Add full name
        drivers = drivers.map((d) => {
          return { name: `${d.firstName} ${d.lastName}`, ...d };
        });

        // Sort drivers by type tier, then alphabetically by first name within
        // each tier: Packers first, then Roll Offs, then Outside Haulers last.
        const driverTypeRank = (d) => {
          if (d.driverTypes.includes("Packer Driver")) return 0;
          if (d.driverTypes.includes("Roll Off Driver")) return 1;
          if (d.driverTypes.includes("Outside Hauler")) return 2;
          return 3;
        };
        drivers.sort((a, b) => {
          const rankDiff = driverTypeRank(a) - driverTypeRank(b);
          if (rankDiff !== 0) return rankDiff;
          // Within same tier, sort alphabetically by first name
          return a.firstName.localeCompare(b.firstName);
        });

        this.drivers = drivers;
      } catch (error) {
        this.showSnackbar("Error fetching drivers", "error");
      }
    },

    buildScheduleGrid() {
      // Build the local schedule grid from existing scheduled requests
      const schedule = {};

      // Load scheduled requests (existing logic)
      this.scheduledRequestsForDate.forEach((request) => {
        const driverId = request.scheduledDriver && request.scheduledDriver.id;
        const timeSlot = request.scheduledTime;

        if (driverId && timeSlot) {
          if (!schedule[driverId]) {
            schedule[driverId] = {};
          }

          // Convert scheduled time to HH:mm format
          const timeIn24Hour = moment(timeSlot, "h:mm A").format("HH:mm");
          schedule[driverId][timeIn24Hour] = request;
        }
      });

      // Load drafted requests (open with draftDriver + draftTime)
      this.draftedRequestsForDate.forEach((request) => {
        const driverId = request.draftDriver && request.draftDriver.id;
        const draftTime = request.draftTime;

        if (driverId && draftTime) {
          if (!schedule[driverId]) {
            schedule[driverId] = {};
          }
          const timeIn24Hour = moment(draftTime, "h:mm A").format("HH:mm");
          schedule[driverId][timeIn24Hour] = {
            ...request,
            isDraft: true, // Flag to distinguish in UI
          };
        }
      });

      this.localSchedule = schedule;
    },

    isSlotOccupied(driverId, timeSlot) {
      return !!(
        this.localSchedule[driverId] && this.localSchedule[driverId][timeSlot]
      );
    },

    getScheduledJob(driverId, timeSlot) {
      return (
        this.localSchedule[driverId] && this.localSchedule[driverId][timeSlot]
      );
    },

    getDriverScheduledCount(driverId) {
      return this.localSchedule[driverId]
        ? Object.keys(this.localSchedule[driverId]).length
        : 0;
    },

    handleDragStart(event, request) {
      event.dataTransfer.setData("text/plain", JSON.stringify(request));
      event.dataTransfer.effectAllowed = "move";
    },

    handleDragOver(event) {
      event.dataTransfer.dropEffect = "move";
    },

    handleDragEnter(event) {
      event.currentTarget.classList.add("cell-drag-over");
    },

    handleDragLeave(event) {
      // Only remove class if we're actually leaving the element
      if (!event.currentTarget.contains(event.relatedTarget)) {
        event.currentTarget.classList.remove("cell-drag-over");
      }
    },

    handleUnscheduleDragOver(event) {
      event.preventDefault();
    },

    handleUnscheduleDragEnter(event) {
      event.currentTarget.classList.add("unschedule-drag-over");
    },

    handleUnscheduleDragLeave(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        event.currentTarget.classList.remove("unschedule-drag-over");
      }
    },

    handleUnscheduleDrop(event) {
      event.preventDefault();
      event.currentTarget.classList.remove("unschedule-drag-over");

      try {
        const requestData = event.dataTransfer.getData("text/plain");
        if (!requestData) return;

        const request = JSON.parse(requestData);

        // Only handle scheduled jobs being moved back
        if (request.isMoving && request.fromDriverId && request.fromTimeSlot) {
          this.removeFromSchedule(request.fromDriverId, request.fromTimeSlot);
        }
      } catch (error) {
        console.error("Error unscheduling request:", error);
      }
    },

    async handleDrop(event, driverId, timeSlot) {
      event.preventDefault();
      event.currentTarget.classList.remove("cell-drag-over");

      // Check if slot is already occupied
      if (this.isSlotOccupied(driverId, timeSlot)) {
        this.showSnackbar("Time slot is already occupied", "error");
        return;
      }

      try {
        const requestData = event.dataTransfer.getData("text/plain");
        if (!requestData) return;

        const request = JSON.parse(requestData);

        // Handle moving from another slot
        if (request.isMoving && request.fromDriverId && request.fromTimeSlot) {
          // Remove from old position
          this.$delete(
            this.localSchedule[request.fromDriverId],
            request.fromTimeSlot
          );
        }

        // Initialize driver schedule if needed
        if (!this.localSchedule[driverId]) {
          this.$set(this.localSchedule, driverId, {});
        }

        // Add request to time slot (local state)
        const timeSlotObj = this.timeSlots.find(
          (slot) => slot.value === timeSlot
        );
        const driver = this.drivers.find((d) => d.id === driverId);
        const isDraft = request.status !== "scheduled" && request.status !== "closed";

        this.$set(this.localSchedule[driverId], timeSlot, {
          ...request,
          scheduledTime: timeSlotObj ? timeSlotObj.display : timeSlot,
          scheduledDriver: driver,
          draftDriver: isDraft ? driver : request.draftDriver,
          draftTime: isDraft ? (timeSlotObj ? timeSlotObj.display : timeSlot) : request.draftTime,
          isDraft: isDraft,
        });

        // Persist to Firestore
        if (request.status === "scheduled") {
          // Update scheduled fields immediately for already scheduled requests
          await fb.db
            .collection("truckRequests")
            .doc(request.orderNumber)
            .update({
              scheduledDriver: driver,
              scheduledTime: timeSlotObj ? timeSlotObj.display : timeSlot,
            });
        } else {
          // Save as draft for open requests
          await this.saveDraft(request.orderNumber, driverId, timeSlot);
        }

        // Handle removal from unscheduled lists
        if (!request.isMoving) {
          // Remove from locally unscheduled list if present
          const localIndex = this.locallyUnscheduled.findIndex(
            (r) => r.orderNumber === request.orderNumber
          );
          if (localIndex > -1) {
            this.locallyUnscheduled.splice(localIndex, 1);
          }

          // Add to locally scheduled tracking
          if (!this.locallyScheduled.includes(request.orderNumber)) {
            this.locallyScheduled.push(request.orderNumber);
          }
        }

        const action = request.isMoving ? "Moved job to" : "Assigned job to";
        this.showSnackbar(
          `${action} ${driver ? driver.name : "driver"}`,
          "success"
        );
      } catch (error) {
        this.showSnackbar("Error scheduling request", "error");
      }
    },

    handleScheduledDragStart(event, request, driverId, timeSlot) {
      // Include position information for moving within schedule
      const dragData = {
        ...request,
        fromDriverId: driverId,
        fromTimeSlot: timeSlot,
        isMoving: true,
      };
      event.dataTransfer.setData("text/plain", JSON.stringify(dragData));
      event.dataTransfer.effectAllowed = "move";
    },

    async removeFromSchedule(driverId, timeSlot) {
      const job = this.getScheduledJob(driverId, timeSlot);
      if (job) {
        // Remove from schedule
        this.$delete(this.localSchedule[driverId], timeSlot);

        // Add back to locally unscheduled list if it's not originally scheduled
        if (job.status === "open") {
          // Remove from locally scheduled tracking
          const scheduledIndex = this.locallyScheduled.indexOf(job.orderNumber);
          if (scheduledIndex > -1) {
            this.locallyScheduled.splice(scheduledIndex, 1);
          }

          // Check if it's not already in the locally unscheduled list
          const exists = this.locallyUnscheduled.find(
            (r) => r.orderNumber === job.orderNumber
          );
          if (!exists) {
            this.locallyUnscheduled.push(job);
          }

          // Clear draft fields in Firestore
          await this.clearDraft(job.orderNumber);
        }

        const driver = this.drivers.find((d) => d.id === driverId);
        this.showSnackbar(
          `Unscheduled job from ${driver ? driver.name : "driver"}`,
          "info"
        );
      }
    },

    async saveDraft(orderNumber, driverId, timeSlot) {
      const driver = this.drivers.find((d) => d.id === driverId);
      const timeSlotObj = this.timeSlots.find(
        (slot) => slot.value === timeSlot
      );

      await fb.db
        .collection("truckRequests")
        .doc(orderNumber)
        .update({
          draftDriver: driver,
          draftTime: timeSlotObj ? timeSlotObj.display : timeSlot,
        });
    },

    async clearDraft(orderNumber) {
      await fb.db
        .collection("truckRequests")
        .doc(orderNumber)
        .update({
          draftDriver: null,
          draftTime: null,
        });
    },

    isJobUnlocked(orderNumber) {
      return this.unlockedJobs.includes(orderNumber);
    },

    toggleJobLock(orderNumber) {
      const index = this.unlockedJobs.indexOf(orderNumber);
      if (index > -1) {
        this.unlockedJobs.splice(index, 1);
        this.showSnackbar("Job locked", "info");
      } else {
        this.unlockedJobs.push(orderNumber);
        this.showSnackbar("Job unlocked - you can now move it", "success");
      }
    },

    isJobDraggable(job) {
      // Closed jobs are never draggable
      if (job.status === "closed") return false;
      // Drafts and open jobs are always draggable
      if (job.isDraft || job.status === "open") return true;
      // Scheduled jobs are only draggable if unlocked
      if (job.status === "scheduled") {
        return this.isJobUnlocked(job.orderNumber);
      }
      return true;
    },

    getBoroughColor(jobSite) {
      if (!jobSite) return "#cccccc";

      const addressText = `${jobSite.address || ""} ${jobSite.address2 ||
        ""}`.toLowerCase();

      if (addressText.includes("bronx")) {
        return "#2196F3"; // Blue
      } else if (addressText.includes("queens")) {
        return "#FF9800"; // Orange
      } else if (addressText.includes("brooklyn")) {
        return "#F44336"; // Red
      } else if (addressText.includes("manhattan")) {
        return "#9E9E9E"; // Gray (instead of white for visibility)
      } else if (addressText.includes("staten island")) {
        return "#795548"; // Brown
      }

      return "#cccccc"; // Default gray
    },

    showSnackbar(message, color = "info") {
      this.snackText = message;
      this.snackColor = color;
      this.snack = true;
    },

    openJobDetails(job) {
      this.selectedJob = job;
      this.jobDetailsDialog = true;
    },

    formatJobQty(request) {
      if (!request) return "";

      // Mini Pickup/Dropoff
      if (request.requestType === "Mini Pickup/Dropoff") {
        const parts = [];
        if (request.miniPickupQty) parts.push(`P:${request.miniPickupQty}`);
        if (request.miniDropoffQty) parts.push(`D:${request.miniDropoffQty}`);
        return parts.join(" ") || "";
      }

      // Roll Off - show pickup type + container size
      if (request.requestType === "Roll Off") {
        const parts = [];
        if (request.pickupType) parts.push(request.pickupType);
        if (request.qty) parts.push(`${request.qty}yd`);
        return parts.join(" ") || "";
      }

      // Packer - Full load
      if (request.fullLoad) {
        const qty = request.fullLoadsQty || 1;
        return qty > 1 ? `${qty} Full Loads` : "Full Load";
      }

      // Packer - Loose Yards, Mini Containers, or Garbage Cans
      if (request.qty && request.qtyType) {
        if (request.qtyType === "Yards") {
          return `${request.qty} yds`;
        }
        if (request.qtyType === "Mini Containers") {
          return `${request.qty} minis`;
        }
        if (request.qtyType === "Garbage Cans") {
          return `${request.qty} cans`;
        }
        return `${request.qty} ${request.qtyType}`;
      }

      return "";
    },

    loadWeightToTons(loadWeight, loadWeightType) {
      if (loadWeight === null || loadWeight === undefined || loadWeight === "") return 0;
      // Cubic Yards is a volume unit, not weight — exclude from weight totals.
      // Matches createCSV in functions/src/index.ts which emits Cubic Yards to a
      // separate LOAD WEIGHT column and skips them in the POUNDS column.
      if (loadWeightType === "Cubic Yards") return 0;
      const num = Number(loadWeight);
      if (isNaN(num)) return 0;
      // Magnitude heuristic used across the app (createCSV, TruckRequestReports,
      // JobCost): > 100 means already in pounds, otherwise treat as tons.
      return num > 100 ? num / 2000 : num;
    },

    // Quantity for closed jobs on the printable schedule. Mirrors the priority
    // used by displayQuantity in functions/src/index.ts (createCSV / PDF template):
    //   1) driverQuantityBreakdown (Packer close-out)
    //   2) Mini Pickup/Dropoff pickup/dropoff counts
    //   3) closingQty + qtyType fallback
    // Falls back to formatJobQty for Roll Off (container size doesn't change
    // at close) or anything else without close-out fields.
    formatClosedJobQty(job) {
      // Roll Off — pickupType (Swap / Live Load / Pickup / Drop Off Container)
      // and container size don't change at close-out, but we MUST surface the
      // pickupType so dispatch knows what was actually done. Without this
      // branch, Roll Offs hit the closingQty fallback and render as just
      // "30 yds", hiding whether it was a swap, live load, pickup, etc.
      if (job.requestType === "Roll Off") {
        const parts = [];
        if (job.pickupType) parts.push(job.pickupType);
        if (job.qty) parts.push(`${job.qty}yd`);
        return parts.join(" ") || "";
      }

      const breakdown = job.driverQuantityBreakdown;
      if (breakdown) {
        const parts = [];
        if (breakdown.looseYardsRegular > 0)
          parts.push(`${breakdown.looseYardsRegular} yds`);
        if (breakdown.looseYardsHeavy > 0)
          parts.push(`${breakdown.looseYardsHeavy} yds H`);
        if (breakdown.minisRegular > 0)
          parts.push(`${breakdown.minisRegular} minis`);
        if (breakdown.minisHeavy > 0)
          parts.push(`${breakdown.minisHeavy} minis H`);
        if (parts.length) return parts.join(" ");
      }

      if (job.requestType === "Mini Pickup/Dropoff") {
        const parts = [];
        const pickupQty = Math.abs(job.miniPickupQty || 0);
        const dropoffQty = Math.abs(job.miniDropoffQty || 0);
        if (pickupQty > 0) parts.push(`P:${pickupQty}`);
        if (dropoffQty > 0) parts.push(`D:${dropoffQty}`);
        if (parts.length) return parts.join(" ");
      }

      if (job.closingQty) {
        if (job.qtyType === "Yards") return `${job.closingQty} yds`;
        if (job.qtyType === "Mini Containers") return `${job.closingQty} minis`;
        if (job.qtyType === "Garbage Cans") return `${job.closingQty} cans`;
        return `${job.closingQty} ${job.qtyType || ""}`.trim();
      }

      // Roll Off and any other case — fall back to requested formatter
      // (container size / pickup type don't change at close-out).
      return this.formatJobQty(job);
    },

    aggregateDumpTickets(job) {
      if (!job || !job.dumpTickets) return { tons: 0, dockets: [] };
      const tickets = Object.values(job.dumpTickets);
      let tons = 0;
      const dockets = [];
      tickets.forEach((t) => {
        if (!t) return;
        tons += this.loadWeightToTons(t.loadWeight, t.loadWeightType);
        if (t.ticketNumber) dockets.push(String(t.ticketNumber));
      });
      return { tons: Math.round(tons * 100) / 100, dockets };
    },

    formatActualRange(job) {
      if (!job) return "";
      const toMoment = (t) => {
        if (!t) return null;
        if (typeof t.toDate === "function") return moment(t.toDate());
        return moment(t);
      };
      const arrival = toMoment(job.arrivalTime);
      const finish = toMoment(job.finishTime);
      if (arrival && finish) return `${arrival.format("h:mma")}–${finish.format("h:mma")}`;
      if (arrival) return `${arrival.format("h:mma")} (arr)`;
      if (finish) return `${finish.format("h:mma")} (fin)`;
      return "";
    },

    getScheduleByDriverType(driverType) {
      const jobs = [];

      // Get all scheduled jobs from localSchedule
      Object.keys(this.localSchedule).forEach((driverId) => {
        const driver = this.drivers.find((d) => d.id === driverId);

        // Check if driver has the specified driver type
        if (
          driver &&
          driver.driverTypes &&
          driver.driverTypes.includes(driverType)
        ) {
          const driverJobs = this.localSchedule[driverId];

          Object.keys(driverJobs).forEach((timeSlot) => {
            const job = driverJobs[timeSlot];

            // Format job details - use full name since some drivers share first names
            const fullName = driver.name
              ? driver.name.toUpperCase()
              : `${driver.firstName || ""} ${driver.lastName || ""}`.trim().toUpperCase();
            // Show "External" for job IDs that start with "TR" or "(TR" (external truck requests)
            const rawJobId = job.jobSite.jobId || "";
            const upperJobId = rawJobId.toUpperCase();
            const isExternal = upperJobId.startsWith("TR") || upperJobId.startsWith("(TR");
            const jobId = isExternal ? "External" : rawJobId;
            const formattedJob = {
              address:
                job.jobSite.street || job.jobSite.address || "Unknown Address",
              client: job.jobSite.client ? job.jobSite.client.name : null,
              jobId: jobId,
              time: moment(timeSlot, "HH:mm").format("h:mm A"),
              qty: this.formatJobQty(job),
              details: this.formatJobDetails(job),
              driver: fullName,
              scheduledTime: timeSlot, // For sorting
            };

            jobs.push(formattedJob);
          });
        }
      });

      // Sort jobs by driver name alphabetically, then by time within each driver
      jobs.sort((a, b) => {
        // First sort by driver name
        const driverCompare = a.driver.localeCompare(b.driver);
        if (driverCompare !== 0) return driverCompare;
        // Then sort by time within the same driver
        const timeA = moment(a.scheduledTime, "HH:mm");
        const timeB = moment(b.scheduledTime, "HH:mm");
        return timeA.diff(timeB);
      });

      return jobs;
    },

    formatJobDetails(job) {
      const details = [];

      // Add truck type details
      if (job.truckType) {
        details.push(job.truckType);
      }

      // Add quantity/size
      if (job.truckQty && job.truckQty > 1) {
        details.push(`${job.truckQty} trucks`);
      }

      // Add container size if available
      if (job.containerSize) {
        details.push(job.containerSize);
      }

      // Add duration if available
      if (job.duration) {
        details.push(`(${job.duration})`);
      }

      // Add notes if specified and short
      if (job.notes && job.notes.length < 30) {
        details.push(job.notes);
      }

      return details.join(" ") || "Standard";
    },

    showPreviewDialog() {
      this.previewDialog = true;
    },

    async loadDriverHoursBackground() {
      try {
        // Calculate current ISO week range (Monday 00:00 → Sunday 23:59)
        const weekStart = moment().startOf("isoWeek").toDate();
        const weekEnd = moment().endOf("isoWeek").toDate();
        const weekStartTs = firebase.firestore.Timestamp.fromDate(weekStart);
        const weekEndTs = firebase.firestore.Timestamp.fromDate(weekEnd);

        // Fetch all employees
        const employeesSnap = await fb.db.collection("employees").get();
        const employees = [];
        employeesSnap.forEach((doc) => {
          const data = doc.data();
          employees.push({
            id: doc.id,
            firstName: (data.firstName || "").toLowerCase(),
            lastName: (data.lastName || "").toLowerCase(),
          });
        });

        // Match each driver to an employee docId using fuzzy name matching
        // Employee names may be longer (e.g., driver "Segundo Salazar" → employee "Segundo Rodrigo Salazar")
        const driverResults = this.drivers.map((driver) => {
          const driverFirst = (driver.firstName || "").toLowerCase().trim();
          const driverLast = (driver.lastName || "").toLowerCase().trim();

          const lastNameMatches = employees.filter((e) => e.lastName === driverLast);
          let match = null;
          if (lastNameMatches.length === 1) {
            match = lastNameMatches[0];
          } else if (lastNameMatches.length > 1) {
            match =
              lastNameMatches.find((e) => e.firstName === driverFirst) ||
              lastNameMatches.find((e) => e.firstName.includes(driverFirst) || driverFirst.includes(e.firstName)) ||
              null;
          }

          return {
            driverId: driver.id,
            name: driver.name,
            employeeId: match ? match.id : null,
            matched: !!match,
            hours: 0,
          };
        });

        // Collect all matched employee IDs
        const matchedIds = driverResults
          .filter((d) => d.matched)
          .map((d) => d.employeeId);

        // Query punchRecords in chunks of 10 (Firestore 'in' operator limit)
        const allPunches = {};
        for (let i = 0; i < matchedIds.length; i += 10) {
          const chunk = matchedIds.slice(i, i + 10);
          const punchSnap = await fb.db
            .collection("punchRecords")
            .where("employee", "in", chunk)
            .where("time", ">=", weekStartTs)
            .where("time", "<=", weekEndTs)
            .orderBy("time", "asc")
            .get();

          punchSnap.forEach((doc) => {
            const data = doc.data();
            const empId = data.employee;
            if (!allPunches[empId]) allPunches[empId] = [];
            allPunches[empId].push({
              direction: data.direction,
              time: data.time,
            });
          });
        }

        // Calculate hours for each matched driver
        const hoursMap = {};
        driverResults.forEach((driver) => {
          if (!driver.matched) return;
          const punches = allPunches[driver.employeeId] || [];
          let totalSeconds = 0;
          let i = 0;
          while (i < punches.length) {
            if (punches[i].direction === "in") {
              if (i + 1 < punches.length && punches[i + 1].direction === "out") {
                totalSeconds += punches[i + 1].time.seconds - punches[i].time.seconds;
                i += 2;
              } else if (i === punches.length - 1) {
                // Trailing "in" with no "out" — currently clocked in, count up to now
                const elapsedSeconds = Math.floor(Date.now() / 1000) - punches[i].time.seconds;
                // Only count if under 10 hours to avoid missed clock-outs bleeding across days
                if (elapsedSeconds < 36000) {
                  totalSeconds += elapsedSeconds;
                }
                i++;
              } else {
                // Orphan "in" followed by another "in" (missed clock-out) — skip
                i++;
              }
            } else {
              // Orphan "out" — skip
              i++;
            }
          }
          driver.hours = totalSeconds / 3600;
          hoursMap[driver.driverId] = driver.hours;
        });

        // Update map for inline display (Vue reactivity requires $set or replacing the object)
        this.driverHoursMap = hoursMap;

        return driverResults;
      } catch (error) {
        console.error("Error loading driver hours:", error);
        return [];
      }
    },


    async sendScheduleEmail() {
      try {
        this.showSnackbar("Sending email...", "info");

        const dispatchScheduleEmail = fb.functions.httpsCallable(
          "dispatchScheduleEmail"
        );

        const displayDate = moment(this.selectedDate).format("MMMM D, YYYY");

        await dispatchScheduleEmail({
          packerSchedule: this.packerSchedule,
          rollOffSchedule: this.rollOffSchedule,
          outsideHaulerSchedule: this.outsideHaulerSchedule,
          selectedDate: this.selectedDate,
          displayDate: displayDate,
        });

        this.showSnackbar("Schedule email sent successfully!", "success");
        this.previewDialog = false;
      } catch (error) {
        console.error("Error sending schedule email:", error);
        this.showSnackbar("Failed to send email. Please try again.", "error");
      }
    },

    async finalizeAndSendSchedule() {
      try {
        this.showSnackbar("Finalizing schedule...", "info");

        // Batch update all drafted requests to scheduled
        const batch = fb.db.batch();

        Object.keys(this.localSchedule).forEach((driverId) => {
          const driverJobs = this.localSchedule[driverId];
          Object.keys(driverJobs).forEach((timeSlot) => {
            const job = driverJobs[timeSlot];

            // Only finalize drafts (open status with isDraft flag)
            if (job.isDraft && job.status === "open") {
              const ref = fb.db.collection("truckRequests").doc(job.orderNumber);
              const driver =
                job.draftDriver || this.drivers.find((d) => d.id === driverId);
              batch.update(ref, {
                status: "scheduled",
                scheduledDriver: driver,
                scheduledTime: job.draftTime || job.scheduledTime,
                scheduledDay: {
                  displayDate: moment(this.selectedDate).format("MMM Do (dddd)"),
                  displayDate2: moment(this.selectedDate).format("M-D-YY"),
                  day: this.selectedDate,
                },
                scheduledBy: this.user,
                scheduledDate: fb.timestamp,
                // Clear draft fields after finalizing
                draftDriver: null,
                draftTime: null,
              });
            }
          });
        });

        await batch.commit();

        // Send the email
        await this.sendScheduleEmail();

        // Clear local tracking state
        this.locallyScheduled = [];
        this.locallyUnscheduled = [];

        // Rebuild grid to reflect new scheduled status
        this.buildScheduleGrid();

        this.showSnackbar("Schedule finalized and sent!", "success");
      } catch (error) {
        console.error("Error finalizing schedule:", error);
        this.showSnackbar("Failed to finalize schedule", "error");
      }
    },

    formatPrintJob(job, timeSlot) {
      const jobDetails = this.formatPrintJobDetails(job);
      const rawJobId = job.jobSite.jobId || "";
      const showJobId = rawJobId && !rawJobId.toUpperCase().startsWith("TR");
      const { tons, dockets } = this.aggregateDumpTickets(job);

      const toMoment = (t) => {
        if (!t) return null;
        if (typeof t.toDate === "function") return moment(t.toDate());
        return moment(t);
      };
      const arrivalMoment = toMoment(job.arrivalTime);
      const finishMoment = toMoment(job.finishTime);

      // For closed jobs, show what was actually delivered (driverQuantityBreakdown
       // / closingQty), not the originally requested qty — same convention as
       // createCSV's displayQuantity.
      const qty =
        job.status === "closed"
          ? this.formatClosedJobQty(job)
          : this.formatJobQty(job);

      return {
        address: job.jobSite.street || job.jobSite.address || "",
        jobId: showJobId ? rawJobId : "",
        orderNumber: job.orderNumber || "",
        time: timeSlot ? moment(timeSlot, "HH:mm").format("h:mm A") : "",
        arrivalDisplay: arrivalMoment ? arrivalMoment.format("h:mma") : "",
        finishDisplay: finishMoment ? finishMoment.format("h:mma") : "",
        details: jobDetails,
        client: job.jobSite.client ? job.jobSite.client.name : "",
        qty,
        scheduledTime: timeSlot,
        isCongestionZone: isInCongestionZone(job.jobSite.postalCode),
        status: job.status,
        weightTons: tons,
        dockets,
      };
    },

    getPrintScheduleByDriverType(driverType) {
      const driverJobs = {};

      const driverMatchesType = (driver) =>
        !!(driver && driver.driverTypes && driver.driverTypes.includes(driverType));

      const ensureDriverBucket = (driver) => {
        const driverName = driver.name
          ? driver.name.toUpperCase()
          : (driver.firstName || "").toUpperCase();
        if (!driverJobs[driverName]) driverJobs[driverName] = [];
        return driverName;
      };

      // Add one printed row per request, keyed off the driver + time it's
      // assigned to. This intentionally reads from the raw per-date request
      // lists rather than the collapsed localSchedule grid: localSchedule is a
      // one-job-per-(driver × time-slot) map, so duplicate requests that share
      // a driver and scheduled time (e.g. a driver duplicating a closed dump to
      // run it again) overwrite each other and only one survives. Building the
      // print list from the raw requests gives every duplicate its own line so
      // the evening reconciliation shows all of them.
      // NOTE: de-collision is intentionally print-only — the on-screen grid
      // still shows a single job per slot.
      const addRequest = (request, driverId, rawTime) => {
        const driver = this.drivers.find((d) => d.id === driverId);
        if (!driverMatchesType(driver)) return;
        const driverName = ensureDriverBucket(driver);
        const timeSlot = rawTime
          ? moment(rawTime, "h:mm A").format("HH:mm")
          : "";
        driverJobs[driverName].push(this.formatPrintJob(request, timeSlot));
      };

      // Scheduled + closed requests for the date (mirrors buildScheduleGrid's
      // first loop, minus the single-slot overwrite).
      this.scheduledRequestsForDate.forEach((request) => {
        const driverId = request.scheduledDriver && request.scheduledDriver.id;
        if (driverId && request.scheduledTime) {
          addRequest(request, driverId, request.scheduledTime);
        }
      });

      // Drafted requests (open, with a tentative driver + time) — mirrors
      // buildScheduleGrid's second loop.
      this.draftedRequestsForDate.forEach((request) => {
        const driverId = request.draftDriver && request.draftDriver.id;
        if (driverId && request.draftTime) {
          addRequest(request, driverId, request.draftTime);
        }
      });

      // Cancelled requests assigned to drivers of this type — must stay visible
      // so dispatch can catch missed service jobs and bill them.
      this.cancelledRequestsForDate.forEach((req) => {
        const driverId = req.scheduledDriver && req.scheduledDriver.id;
        if (driverId) {
          addRequest(req, driverId, req.scheduledTime);
        }
      });

      // Sort each driver's jobs by time
      Object.keys(driverJobs).forEach((driverName) => {
        driverJobs[driverName].sort((a, b) => {
          const timeA = moment(a.scheduledTime || "00:00", "HH:mm");
          const timeB = moment(b.scheduledTime || "00:00", "HH:mm");
          return timeA.diff(timeB);
        });
      });

      return driverJobs;
    },

    formatPrintJobDetails(job) {
      const details = [];

      // Simplified details - just essential info
      if (job.containerSize) {
        details.push(job.containerSize);
      } else if (job.truckType) {
        details.push(job.truckType);
      }

      return details.join(" ") || "";
    },

    generateEmptyRows(count) {
      let rows = "";
      for (let i = 0; i < count; i++) {
        rows += `
          <tr class="empty-row">
            <td class="checkbox-cell"><input type="checkbox" /></td>
            <td class="job-cell"></td>
            <td class="checkbox-cell"><input type="checkbox" /></td>
            <td class="checkbox-cell"><input type="checkbox" /></td>
            <td class="checkbox-cell"><input type="checkbox" /></td>
            <td class="text-cell"></td>
            <td class="text-cell"></td>
            <td class="text-cell"></td>
            <td class="text-cell"></td>
            <td class="billed-cell"><input type="checkbox" /></td>
          </tr>
        `;
      }
      return rows;
    },

    generateDriverSection(driverName, jobs, emptyRowCount) {
      let html = `
        <tr class="driver-header-row">
          <td colspan="10" class="driver-name-cell">${driverName}</td>
        </tr>
      `;

      // Add job rows
      jobs.forEach((job) => {
        const isCancelled = job.status === "cancelled";
        const isClosed = job.status === "closed";

        // Job line always shows the scheduled time. Actual arrival/finish goes
        // into the dedicated Add. Time column for closed jobs.
        const jobLineCore = `${job.address} ${job.jobId ? "(" + job.jobId + ")" : ""} ${job.time} ${job.details} ${job.client} ${job.qty || ""}`.trim();
        const cancelledStamp = isCancelled
          ? `<span style="color:#c00; font-weight:bold; margin-right:4px;">CANCELLED</span>`
          : "";
        const jobLineHTML = isCancelled
          ? `${cancelledStamp}<span style="text-decoration: line-through;">${jobLineCore}</span>`
          : jobLineCore;

        const rowStyle = isCancelled
          ? ' style="background-color: #FFE0B2;"'
          : "";
        const congestionIndicator = job.isCongestionZone ? "X" : "";

        // Add. Time — actual arrival/finish range, set when the driver hits
        // "Mark Arrived" / "Mark Finished" in Driver.vue.
        let addTimeCell = "";
        if (isClosed && (job.arrivalDisplay || job.finishDisplay)) {
          if (job.arrivalDisplay && job.finishDisplay) {
            addTimeCell = `${job.arrivalDisplay}–${job.finishDisplay}`;
          } else if (job.arrivalDisplay) {
            addTimeCell = `${job.arrivalDisplay} (arr)`;
          } else {
            addTimeCell = `${job.finishDisplay} (fin)`;
          }
        }

        const weightCell =
          isClosed && job.weightTons ? `${job.weightTons} T` : "";
        const dumpTicketCell =
          isClosed && job.dockets && job.dockets.length
            ? job.dockets.join(", ")
            : "";
        // Docket # = the truck request order number; show on every row.
        const docketCell = job.orderNumber || "";

        html += `
          <tr class="job-row"${rowStyle}>
            <td class="checkbox-cell"><input type="checkbox" /></td>
            <td class="job-cell">${jobLineHTML}</td>
            <td class="checkbox-cell"><input type="checkbox" /></td>
            <td class="checkbox-cell"><input type="checkbox" /></td>
            <td class="checkbox-cell" style="font-weight: bold; color: #c00;${job.isCongestionZone ? ' background-color: #ffd3a7;' : ''}">${congestionIndicator}</td>
            <td class="text-cell">${addTimeCell}</td>
            <td class="text-cell">${weightCell}</td>
            <td class="text-cell">${dumpTicketCell}</td>
            <td class="text-cell">${docketCell}</td>
            <td class="billed-cell"><input type="checkbox" /></td>
          </tr>
        `;
      });

      // Add empty rows for add-ons
      html += this.generateEmptyRows(emptyRowCount);

      return html;
    },

    printSchedule() {
      const formattedDate = moment(this.selectedDate).format("M-D-YY");
      const displayDate = moment(this.selectedDate).format("MMMM D, YYYY");

      // Get jobs organized by driver type
      const packerJobs = this.getPrintScheduleByDriverType("Packer Driver");
      const rollOffJobs = this.getPrintScheduleByDriverType("Roll Off Driver");
      const outsideHaulerJobs = this.getPrintScheduleByDriverType(
        "Outside Hauler"
      );

      // Separate Victor from other packer drivers (find any driver whose name starts with VICTOR)
      let victorKey = null;
      let victorJobs = [];
      Object.keys(packerJobs).forEach((driverName) => {
        if (driverName.startsWith("VICTOR")) {
          victorKey = driverName;
          victorJobs = packerJobs[driverName];
        }
      });
      if (victorKey) {
        delete packerJobs[victorKey];
      }

      // Generate HTML
      let html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Dispatch Daily Schedule - ${formattedDate}</title>
          <style>
            @page {
              size: portrait;
              margin: 0.3in;
            }

            * {
              box-sizing: border-box;
            }

            body {
              font-family: Arial, sans-serif;
              font-size: 9px;
              margin: 0;
              padding: 5px;
            }

            .section {
              margin-bottom: 10px;
            }

            .section-title {
              color: #c00;
              font-size: 12px;
              font-weight: bold;
              text-decoration: underline;
              margin-bottom: 3px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 5px;
            }

            th, td {
              border: 1px solid #000;
              padding: 2px 3px;
              text-align: left;
              vertical-align: middle;
            }

            th {
              background-color: #f0f0f0;
              font-weight: bold;
              font-size: 8px;
            }

            .header-row td {
              font-weight: bold;
              background-color: #f5f5f5;
              font-size: 8px;
            }

            .date-cell {
              background-color: #ffff00;
              font-weight: bold;
            }

            .checkbox-cell {
              width: 40px;
              text-align: center;
            }

            .checkbox-cell input[type="checkbox"] {
              width: 10px;
              height: 10px;
            }

            .billed-cell {
              width: 70px;
              text-align: left;
              padding-left: 4px;
            }

            .billed-cell input[type="checkbox"] {
              width: 10px;
              height: 10px;
              margin-right: 3px;
            }

            .job-cell {
              width: 50%;
            }

            .text-cell {
              width: 6%;
            }

            .driver-header-row td {
              font-weight: bold;
              background-color: #fff;
              border-bottom: none;
            }

            .driver-name-cell {
              font-weight: bold;
            }

            .job-row td {
              height: 16px;
            }

            .empty-row td {
              height: 16px;
            }

            .permits-section, .labor-section {
              margin-top: 8px;
            }

            .permits-title, .labor-title {
              color: #c00;
              font-size: 11px;
              font-weight: bold;
              text-decoration: underline;
            }

            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }

              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="no-print" style="margin-bottom: 15px;">
            <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; cursor: pointer;">
              Print Schedule
            </button>
            <button onclick="window.close()" style="padding: 10px 20px; font-size: 14px; cursor: pointer; margin-left: 10px;">
              Close
            </button>
          </div>

          <!-- Demo Packers Section -->
          <div class="section">
            <div class="section-title">Demo Packers</div>
            <table>
              <thead>
                <tr class="header-row">
                  <td>Added to App</td>
                  <td class="date-cell">${formattedDate}</td>
                  <td class="checkbox-cell">PAPER</td>
                  <td class="checkbox-cell">APP</td>
                  <td class="checkbox-cell">CONG</td>
                  <td class="text-cell">Add. Time</td>
                  <td class="text-cell">Weight</td>
                  <td class="text-cell">Dump Ticket #</td>
                  <td class="text-cell">Docket #</td>
                  <td class="billed-cell">BILLED</td>
                </tr>
                <tr>
                  <td colspan="10" style="font-weight: bold;">Demo Packers</td>
                </tr>
              </thead>
              <tbody>
      `;

      // Add packer drivers (excluding Victor)
      const packerDriverNames = Object.keys(packerJobs).sort();
      packerDriverNames.forEach((driverName) => {
        html += this.generateDriverSection(driverName, packerJobs[driverName], 5);
      });

      // Add Victor at the end with 15 empty rows (use full name or default to "VICTOR")
      html += this.generateDriverSection(victorKey || "VICTOR", victorJobs, 15);

      html += `
              </tbody>
            </table>
          </div>

          <!-- Roll Offs Section -->
          <div class="section">
            <div class="section-title">Roll Offs</div>
            <table>
              <thead>
                <tr class="header-row">
                  <td>Added to App</td>
                  <td class="date-cell">${formattedDate}</td>
                  <td class="checkbox-cell">PAPER</td>
                  <td class="checkbox-cell">APP</td>
                  <td class="checkbox-cell">CONG</td>
                  <td class="text-cell">Add. Time</td>
                  <td class="text-cell">Weight</td>
                  <td class="text-cell">Dump Ticket #</td>
                  <td class="text-cell">Docket #</td>
                  <td class="billed-cell">BILLED</td>
                </tr>
                <tr>
                  <td colspan="10" style="font-weight: bold;">Roll Offs</td>
                </tr>
              </thead>
              <tbody>
      `;

      // Add roll off drivers
      const rollOffDriverNames = Object.keys(rollOffJobs).sort();
      rollOffDriverNames.forEach((driverName) => {
        html += this.generateDriverSection(driverName, rollOffJobs[driverName], 5);
      });

      // Add some empty rows if no roll off jobs
      if (rollOffDriverNames.length === 0) {
        html += this.generateEmptyRows(10);
      }

      html += `
              </tbody>
            </table>
          </div>
      `;

      // Outside Haulers Section — only render when there are haulers scheduled
      const outsideHaulerDriverNames = Object.keys(outsideHaulerJobs).sort();
      if (outsideHaulerDriverNames.length > 0) {
        html += `
          <!-- Outside Haulers Section -->
          <div class="section">
            <div class="section-title">Outside Haulers</div>
            <table>
              <thead>
                <tr class="header-row">
                  <td>Added to App</td>
                  <td class="date-cell">${formattedDate}</td>
                  <td class="checkbox-cell">PAPER</td>
                  <td class="checkbox-cell">APP</td>
                  <td class="checkbox-cell">CONG</td>
                  <td class="text-cell">Add. Time</td>
                  <td class="text-cell">Weight</td>
                  <td class="text-cell">Dump Ticket #</td>
                  <td class="text-cell">Docket #</td>
                  <td class="billed-cell">BILLED</td>
                </tr>
                <tr>
                  <td colspan="10" style="font-weight: bold;">Outside Haulers</td>
                </tr>
              </thead>
              <tbody>
        `;
        outsideHaulerDriverNames.forEach((driverName) => {
          html += this.generateDriverSection(
            driverName,
            outsideHaulerJobs[driverName],
            5
          );
        });
        html += `
              </tbody>
            </table>
          </div>
        `;
      }

      html += `
          <!-- Permits Section -->
          <div class="section permits-section">
            <table>
              <thead>
                <tr>
                  <td class="permits-title" style="border: 1px solid #000;">Permits</td>
                  <td style="border: 1px solid #000; width: 80%;"></td>
                  <td class="billed-cell" style="border: 1px solid #000;">BILLED</td>
                </tr>
              </thead>
              <tbody>
                <tr><td></td><td></td><td class="billed-cell"><input type="checkbox" /></td></tr>
                <tr><td></td><td></td><td class="billed-cell"><input type="checkbox" /></td></tr>
                <tr><td></td><td></td><td class="billed-cell"><input type="checkbox" /></td></tr>
                <tr><td></td><td></td><td class="billed-cell"><input type="checkbox" /></td></tr>
              </tbody>
            </table>
          </div>

          <!-- Labor Section -->
          <div class="section labor-section">
            <table>
              <thead>
                <tr>
                  <td class="labor-title" style="border: 1px solid #000;">Labor</td>
                  <td style="border: 1px solid #000; width: 80%;"></td>
                  <td class="billed-cell" style="border: 1px solid #000;">BILLED</td>
                </tr>
              </thead>
              <tbody>
                <tr><td></td><td></td><td class="billed-cell"><input type="checkbox" /></td></tr>
                <tr><td></td><td></td><td class="billed-cell"><input type="checkbox" /></td></tr>
                <tr><td></td><td></td><td class="billed-cell"><input type="checkbox" /></td></tr>
                <tr><td></td><td></td><td class="billed-cell"><input type="checkbox" /></td></tr>
              </tbody>
            </table>
          </div>
        </body>
        </html>
      `;

      // Open in new window
      const printWindow = window.open("", "_blank");
      printWindow.document.write(html);
      printWindow.document.close();
    },
  },
  watch: {
    selectedDate() {
      this.buildScheduleGrid();
      // Clear local state when date changes
      this.locallyUnscheduled = [];
      this.locallyScheduled = [];
    },
  },
};
</script>

<style scoped>
.scheduler-layout {
  display: flex;
  height: calc(100vh - 195px);
  min-height: 400px;
}

.unscheduled-panel {
  width: 250px;
  min-width: 250px;
  border-right: 2px solid #e0e0e0;
  background: #fafafa;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 12px;
  background: #f5f5f5;
  border-bottom: 2px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
}

.panel-title {
  font-weight: bold;
  font-size: 14px;
}

.unscheduled-list-vertical {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.schedule-panel {
  flex: 1;
  overflow: hidden;
}

.schedule-wrapper {
  height: 100%;
  overflow: auto;
  position: relative;
}

.schedule-container {
  min-width: fit-content;
  display: inline-block;
  width: 100%;
}

.schedule-header {
  display: flex;
  background: #f5f5f5;
  border-bottom: 2px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 3;
  min-width: 100%;
}

.time-header {
  width: 55px;
  min-width: 55px;
  padding: 12px 4px;
  font-weight: bold;
  font-size: 12px;
  text-align: center;
  border-right: 2px solid #e0e0e0;
  background: #f5f5f5;
  position: sticky;
  left: 0;
  z-index: 4;
  flex-shrink: 0;
}

.driver-header {
  min-width: 130px;
  max-width: 130px;
  width: 130px;
  padding: 6px 4px;
  text-align: center;
  border-right: 1px solid #e0e0e0;
  background: #f5f5f5;
  flex-shrink: 0;
}

.driver-name {
  font-weight: bold;
  font-size: 11px;
  margin-bottom: 2px;
  word-wrap: break-word;
}

.driver-stats {
  font-size: 11px;
  color: #666;
}

.schedule-body {
  background: white;
  min-width: 100%;
}

.schedule-row {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  min-width: 100%;
}

.time-cell {
  width: 55px;
  min-width: 55px;
  padding: 2px;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  background: #fafafa;
  border-right: 2px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  position: sticky;
  left: 0;
  z-index: 2;
  flex-shrink: 0;
}

.schedule-cell {
  min-width: 130px;
  max-width: 130px;
  width: 130px;
  min-height: 60px;
  border-right: 1px solid #e0e0e0;
  position: relative;
  transition: background-color 0.2s;
  padding: 2px;
  flex-shrink: 0;
}

.schedule-cell:hover {
  background-color: #f0f8ff;
}

.schedule-cell.cell-occupied {
  background-color: #e8f5e8;
}

.schedule-cell.cell-drag-over {
  background-color: #e1f5fe !important;
  border: 2px dashed #2196f3 !important;
}

.job-card {
  margin: 1px;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.2;
  cursor: grab;
  transition: all 0.2s;
}

.job-card:active {
  cursor: grabbing;
}

.job-card.scheduled {
  background: #e3f2fd;
  border: 1px solid #2196f3;
  height: calc(100% - 4px);
  position: relative;
}

/* Minimal view styles */
.job-minimal {
  display: flex;
  flex-direction: column;
}

.job-time-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.job-time {
  font-weight: 600;
  color: #1565c0;
  font-size: 11px;
}

.job-address-minimal {
  font-size: 10px;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.job-actual-range {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 600;
  color: #2e7d32;
  line-height: 1.3;
  margin-top: 2px;
}

.job-actual-icon {
  color: #2e7d32 !important;
  font-size: 12px !important;
}

.job-qty {
  font-size: 10px;
  font-weight: 600;
  color: #e65100;
}

.job-time.clickable {
  cursor: pointer;
  text-decoration: underline;
}

.job-time.clickable:hover {
  color: #0d47a1;
}

/* Unschedule drop zone */
.unscheduled-list-vertical.unschedule-drag-over {
  background-color: #fff3e0;
  border: 2px dashed #ff9800;
  border-radius: 4px;
}

/* Job details dialog */
.job-details-row {
  margin-bottom: 8px;
  font-size: 14px;
}

.job-card.job-closed {
  opacity: 0.4;
  pointer-events: none;
}

.job-card.job-draft {
  background: #fff8e1;
  border: 1px dashed #ff9800;
}

.job-card.job-locked {
  cursor: default;
  opacity: 0.85;
}

.job-card.job-locked:hover {
  transform: none;
  box-shadow: none;
}

.job-lock-icon {
  color: #9e9e9e;
  margin-left: 2px;
}

.job-card.unscheduled {
  background: #fff3e0;
  border: 1px solid #ff9800;
  margin: 4px 0;
  display: block;
}

.job-card.unscheduled.vertical {
  width: 100%;
  margin: 0 0 8px 0;
}

.job-card.unscheduled:hover {
  background: #ffe0b2;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.job-address {
  font-weight: bold;
  color: #1976d2;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-details {
  color: #666;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-duration {
  font-size: 10px;
  color: #888;
}

.job-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2px;
}

.job-order {
  font-weight: bold;
  color: #d32f2f;
}

.job-time {
  color: #1976d2;
  font-size: 10px;
}

.job-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 10px;
  color: #666;
}

.job-header-scheduled {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 10px;
}

.job-original-time {
  color: #1976d2;
  font-weight: 500;
}

.job-site-id {
  font-size: 10px;
  color: #666;
  margin-top: 1px;
}

.job-type {
  font-weight: 500;
}

.no-requests {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: #999;
  text-align: center;
  padding: 20px;
}

.borough-legend {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-left: 8px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.legend-text {
  font-size: 11px;
  color: #666;
}

@media (max-width: 768px) {
  .scheduler-layout {
    flex-direction: column;
    height: auto;
  }

  .unscheduled-panel {
    width: 100%;
    height: 200px;
    border-right: none;
    border-bottom: 2px solid #e0e0e0;
  }

  .unscheduled-list-vertical {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 8px;
  }

  .job-card.unscheduled.vertical {
    width: 200px;
    min-width: 200px;
    margin: 0 8px 0 0;
  }

  .schedule-panel {
    height: 400px;
  }

  .driver-name {
    font-size: 11px;
  }

  .borough-legend {
    flex-wrap: wrap;
    margin-top: 8px;
  }
}

/* Preview Dialog Styles */
.preview-section-header {
  font-size: 20px;
  font-weight: bold;
  color: #1976d2;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #1976d2;
}

.schedule-preview-table {
  border: 1px solid #e0e0e0;
  margin-bottom: 24px;
}

.schedule-preview-table thead th {
  background-color: #f5f5f5 !important;
  font-weight: bold !important;
  font-size: 14px !important;
  color: #424242 !important;
  border-bottom: 2px solid #1976d2 !important;
  padding: 12px !important;
}

.schedule-preview-table .col-address {
  width: 40%;
}

.schedule-preview-table .col-job-id {
  width: 12%;
}

.schedule-preview-table .col-time {
  width: 12%;
}

.schedule-preview-table .col-details {
  width: 23%;
}

.schedule-preview-table .col-driver {
  width: 13%;
}

.schedule-preview-table tbody td {
  font-size: 14px !important;
  padding: 10px 12px !important;
  border-bottom: 1px solid #e0e0e0 !important;
}

.schedule-preview-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.schedule-preview-table .job-id-cell {
  color: #1976d2;
  font-weight: 500;
}

.schedule-preview-table .driver-cell {
  font-weight: 600;
  color: #d32f2f;
}

.schedule-preview-table .qty-text {
  font-size: 12px;
  color: #424242;
  font-weight: 500;
  margin-top: 2px;
}

.schedule-preview-table .client-name {
  font-size: 12px;
  color: #757575;
  font-style: italic;
  margin-top: 2px;
}

/* Print styles */
@media print {
  .v-dialog {
    position: static !important;
  }

  .v-card__title,
  .v-card__actions,
  .v-divider {
    display: none !important;
  }

  .v-card {
    box-shadow: none !important;
  }

  .schedule-preview-table tbody tr {
    page-break-inside: avoid;
  }

  .preview-section-header {
    page-break-after: avoid;
  }
}
</style>
